/* @jsx h */
import { defineComponent, h, ref, PropType } from "vue";
import { RightSheet, ResponsiveTable } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { TaxRate } from "@lemmonhq/types";
import { useToast } from "@app/vue/providers/Toast";
import {
  taxSettingsAPI,
  type CreateTaxRateData,
} from "@app/data/api/tax_settings";
import dayjs from "dayjs";
import { formatDateTime, defaultRateForm, type RateFormState } from "./utils";

export const RatesTab = defineComponent({
  name: "RatesTab",
  props: {
    rates: { type: Array as PropType<TaxRate[]>, required: true },
    onRefresh: {
      type: Function as PropType<() => Promise<void>>,
      required: true,
    },
  },
  setup(props) {
    const toast = useToast();
    const isSheetOpen = ref(false);
    const selectedRate = ref<TaxRate | null>(null);
    const mode = ref<"create" | "view">("create");
    const form = ref<RateFormState>(defaultRateForm());
    const submitting = ref(false);

    const openCreate = () => {
      mode.value = "create";
      selectedRate.value = null;
      form.value = defaultRateForm();
      isSheetOpen.value = true;
    };

    const openView = (rate: TaxRate) => {
      mode.value = "view";
      selectedRate.value = rate;
      isSheetOpen.value = true;
    };

    const closeSheet = () => {
      isSheetOpen.value = false;
      selectedRate.value = null;
    };

    const handleCreate = async (event: Event) => {
      event.preventDefault();
      if (submitting.value) return;

      const cleaned: CreateTaxRateData = {
        type: form.value.type.trim(),
        name: form.value.name.trim(),
        value: parseFloat(form.value.value),
        valid_from: dayjs(form.value.validFrom).toISOString(),
        valid_till: form.value.validTill
          ? dayjs(form.value.validTill).toISOString()
          : undefined,
        jurisdiction: form.value.jurisdiction.trim() || undefined,
      };

      if (
        !cleaned.type ||
        !cleaned.name ||
        Number.isNaN(cleaned.value) ||
        !form.value.validFrom
      ) {
        toast.showToast(
          "Complete the name, type, value and start date to create a tax rate.",
          { type: "warning" },
        );
        return;
      }

      try {
        submitting.value = true;
        await taxSettingsAPI.createTaxRate(cleaned);
        await props.onRefresh();
        toast.showToast("Tax rate added successfully.", { type: "success" });
        closeSheet();
      } catch (error) {
        console.error("Failed to create tax rate", error);
        toast.showToast(
          "We could not save that rate. Double-check the details and try again.",
          { type: "error" },
        );
      } finally {
        submitting.value = false;
      }
    };

    const handleArchive = async () => {
      if (submitting.value || !selectedRate.value?.restaurant_id) return;
      try {
        submitting.value = true;
        await taxSettingsAPI.softDeleteTaxRate(selectedRate.value.id);
        await props.onRefresh();
        toast.showToast(
          "Tax rate archived. Existing checks keep historic values.",
          { type: "success" },
        );
        closeSheet();
      } catch (error) {
        console.error("Failed to archive tax rate", error);
        toast.showToast("We could not archive that rate. Please try again.", {
          type: "error",
        });
      } finally {
        submitting.value = false;
      }
    };

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-name",
        label: "Name",
        icon: "show_chart",
        mobilePriority: "hidden",
        render: (rate: TaxRate) => (
          <div>
            <div class="font-medium text-base-content">{rate.name}</div>
            <div class="text-xs text-base-content/60">{rate.type}</div>
          </div>
        ),
      },
      {
        key: "d-value",
        label: "Rate",
        icon: "percent",
        mobilePriority: "hidden",
        render: (rate: TaxRate) => (
          <span class="font-semibold text-base-content">
            {Number(rate.value).toFixed(2)}%
          </span>
        ),
      },
      {
        key: "d-status",
        label: "Status",
        icon: "circle",
        mobilePriority: "hidden",
        render: (rate: TaxRate) => (
          <span
            class={`badge badge-sm ${rate.is_active ? "badge-success" : "badge-neutral"}`}
          >
            {rate.is_active ? "Active" : "Expired"}
          </span>
        ),
      },
      {
        key: "d-valid-from",
        label: "Valid from",
        icon: "schedule",
        mobilePriority: "hidden",
        render: (rate: TaxRate) => (
          <span class="text-sm text-base-content/80">
            {formatDateTime(rate.valid_from)}
          </span>
        ),
      },
      {
        key: "d-valid-till",
        label: "Ends",
        icon: "event",
        mobilePriority: "hidden",
        render: (rate: TaxRate) => (
          <span class="text-sm text-base-content/80">
            {rate.valid_till ? formatDateTime(rate.valid_till) : "—"}
          </span>
        ),
      },
      {
        key: "m-name",
        label: "Name",
        desktopHide: true,
        mobilePriority: "primary",
        render: (rate: TaxRate) => (
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-base-content">
              {rate.name}
            </div>
            <div class="text-xs text-base-content/60">{rate.type}</div>
          </div>
        ),
      },
      {
        key: "m-status",
        label: "Status",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: (rate: TaxRate) => (
          <span
            class={`badge badge-sm ${rate.is_active ? "badge-success" : "badge-neutral"}`}
          >
            {rate.is_active ? "Active" : "Expired"}
          </span>
        ),
      },
      {
        key: "m-value",
        label: "Rate",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (rate: TaxRate) => `${Number(rate.value).toFixed(2)}%`,
      },
      {
        key: "m-valid-from",
        label: "Valid from",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (rate: TaxRate) => formatDateTime(rate.valid_from),
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (rate: TaxRate) => (
          <button
            class="btn btn-sm btn-secondary"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              openView(rate);
            }}
          >
            <span class="material-symbols-outlined text-2xl">open_in_new</span>
          </button>
        ),
      },
    ];

    const renderCreateForm = () => (
      <form class="flex h-full flex-col" onSubmit={handleCreate}>
        <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <p class="text-sm text-base-content/70">
            Configure a percentage and effective window for the rate. This can
            be referenced by tax profiles immediately after creation.
          </p>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="form-control w-full">
              <span class="label-text text-sm font-medium text-base-content/70">
                Rate name
              </span>
              <input
                class="input input-bordered"
                placeholder="Summer VAT"
                value={form.value.name}
                onInput={(e) =>
                  (form.value.name = (e.target as HTMLInputElement).value)
                }
              />
            </label>
            <label class="form-control w-full">
              <span class="label-text text-sm font-medium text-base-content/70">
                Applies to
              </span>
              <input
                class="input input-bordered"
                placeholder="Food service"
                value={form.value.type}
                onInput={(e) =>
                  (form.value.type = (e.target as HTMLInputElement).value)
                }
              />
            </label>
            <label class="form-control w-full">
              <span class="label-text text-sm font-medium text-base-content/70">
                Percentage
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                class="input input-bordered"
                placeholder="8.10"
                value={form.value.value}
                onInput={(e) =>
                  (form.value.value = (e.target as HTMLInputElement).value)
                }
              />
            </label>
            <label class="form-control w-full">
              <span class="label-text text-sm font-medium text-base-content/70">
                Jurisdiction
              </span>
              <input
                class="input input-bordered"
                placeholder="Zürich"
                value={form.value.jurisdiction}
                onInput={(e) =>
                  (form.value.jurisdiction = (
                    e.target as HTMLInputElement
                  ).value)
                }
              />
            </label>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="form-control w-full">
              <span class="label-text text-sm font-medium text-base-content/70">
                Valid from
              </span>
              <input
                type="datetime-local"
                class="input input-bordered"
                value={form.value.validFrom}
                onInput={(e) =>
                  (form.value.validFrom = (e.target as HTMLInputElement).value)
                }
              />
            </label>
            <label class="form-control w-full">
              <span class="label-text text-sm font-medium text-base-content/70">
                Optional end date
              </span>
              <input
                type="datetime-local"
                class="input input-bordered"
                value={form.value.validTill}
                onInput={(e) =>
                  (form.value.validTill = (e.target as HTMLInputElement).value)
                }
              />
            </label>
          </div>
        </div>

        <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex items-center justify-between">
          <p class="text-sm text-base-content/60">
            Rates become available to assign as soon as they're created.
          </p>
          <div class="flex gap-2">
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={closeSheet}
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary btn-sm"
              disabled={submitting.value}
            >
              {submitting.value ? "Saving…" : "Save rate"}
            </button>
          </div>
        </div>
      </form>
    );

    const renderViewDetail = (rate: TaxRate) => (
      <div class="flex h-full flex-col">
        <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <p class="text-sm text-base-content/70">
            Review the current configuration and archive the rate when needed.
            Updates are not permitted to guarantee auditability.
          </p>

          <section class="rounded-2xl border  bg-base-100 p-5 shadow-sm">
            <h3 class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
              Overview
            </h3>
            <dl class="mt-3 grid gap-4 md:grid-cols-2">
              <div>
                <dt class="text-xs uppercase text-base-content/60">
                  Percentage
                </dt>
                <dd class="text-base font-semibold text-base-content">
                  {Number(rate.value).toFixed(2)}%
                </dd>
              </div>
              <div>
                <dt class="text-xs uppercase text-base-content/60">
                  Applies to
                </dt>
                <dd class="text-base text-base-content">{rate.type}</dd>
              </div>
              <div>
                <dt class="text-xs uppercase text-base-content/60">
                  Jurisdiction
                </dt>
                <dd class="text-base text-base-content">
                  {rate.jurisdiction || "All jurisdictions"}
                </dd>
              </div>
              <div>
                <dt class="text-xs uppercase text-base-content/60">Status</dt>
                <dd class="flex items-center gap-2">
                  <span
                    class={`badge ${rate.is_active ? "badge-success" : "badge-outline"}`}
                  >
                    {rate.is_active ? "Active" : "Expired"}
                  </span>
                  {!rate.restaurant_id && (
                    <span class="text-xs text-base-content/60">
                      Default rate
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section class="rounded-2xl border  bg-base-100 p-5 shadow-sm">
            <h3 class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
              Schedule
            </h3>
            <dl class="mt-3 grid gap-3">
              <div>
                <dt class="text-xs uppercase text-base-content/60">
                  Valid from
                </dt>
                <dd class="text-base text-base-content">
                  {formatDateTime(rate.valid_from)}
                </dd>
              </div>
              <div>
                <dt class="text-xs uppercase text-base-content/60">
                  Valid till
                </dt>
                <dd class="text-base text-base-content">
                  {rate.valid_till ? formatDateTime(rate.valid_till) : "Indefinite"}
                </dd>
              </div>
            </dl>
          </section>
        </div>

        <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex items-center justify-between">
          <span class="text-sm text-base-content/60">
            Archive instead of deleting so historical orders remain compliant.
          </span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={closeSheet}
            >
              Close
            </button>
            <button
              type="button"
              class="btn btn-error btn-sm"
              disabled={submitting.value || !rate.restaurant_id}
              onClick={handleArchive}
            >
              {submitting.value ? "Archiving…" : "Archive"}
            </button>
          </div>
        </div>
      </div>
    );

    return () => (
      <>
        <div class="flex items-center justify-end px-4 py-3 border-b border-base-content/5">
          <button class="btn btn-primary btn-sm gap-2" onClick={openCreate}>
            <span class="material-symbols-outlined text-base">add</span>
            New tax rate
          </button>
        </div>

        {props.rates.length === 0 ? (
          <div class="flex min-h-[280px] flex-col items-center justify-center px-8 py-12 text-center">
            <div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-base-200">
              <span class="material-symbols-outlined text-2xl text-base-content/40">
                show_chart
              </span>
            </div>
            <p class="font-medium text-base-content">No custom rates yet</p>
            <p class="mt-1 text-sm text-base-content/60">
              Start by adding your jurisdiction-specific percentages to keep
              compliance fully automated.
            </p>
          </div>
        ) : (
          <ResponsiveTable
            columns={columns}
            rows={props.rates}
            rowKey={(rate: TaxRate) => rate.id}
            onRowClick={openView}
            emptyMessage="No tax rates found"
          />
        )}

        <RightSheet
          isOpen={isSheetOpen.value}
          title={
            mode.value === "create"
              ? "New tax rate"
              : selectedRate.value?.name ?? "Tax rate"
          }
          onClose={closeSheet}
          width="520px"
        >
          {mode.value === "create"
            ? renderCreateForm()
            : selectedRate.value
              ? renderViewDetail(selectedRate.value)
              : null}
        </RightSheet>
      </>
    );
  },
});
